'use client';

/**
 * CRM keyboard grammar — the Linear-style list layer shared by every blotter
 * (subscribers, leads, tasks) plus the pure helpers the command palette and
 * shortcuts overlay build on.
 *
 * Grammar (see components/crm/shortcuts-overlay.tsx for the user-facing sheet):
 *   J / K  or  ArrowDown / ArrowUp   move the focus bar
 *   X                                 toggle-select the focused row
 *   Space                             peek the focused row (onPeek, else onOpen)
 *   Enter                             open the focused row
 *   E                                 complete the focused row (when wired)
 *
 * Scoping rules (non-negotiable):
 *   - NEVER fires while an input / textarea / select / contenteditable or
 *     anything inside a dialog has focus ({@link isGuardedTarget}).
 *   - NEVER fires while a dialog or popover (dialog[open] / aria-modal /
 *     role=dialog) is anywhere in the document ({@link hasOpenModal}) —
 *     ConfirmDialog, FormDialog, Popover menus, the command palette and the
 *     shortcuts overlay all qualify.
 *   - Activation keys (X / Space / Enter / E) NEVER steal from a focused
 *     link, button, or other focusable control ({@link isActivationTarget}) —
 *     Enter on a Tab-focused "Next page" button paginates; Space on a sort
 *     header activates it. J/K still move the focus bar (harmless).
 *   - Never swallows browser chords: any ctrl/meta/alt modifier passes through.
 *   - Single window listener per hook instance; detaches on unmount.
 *
 * The guard predicate and the fuzzy matcher are deliberately pure (duck-typed,
 * no DOM globals) so they unit-test under the node vitest environment — see
 * lib/crm/__tests__/keyboard.test.ts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/* Focus guard (pure)                                                  */
/* ------------------------------------------------------------------ */

/** Structural slice of an event target — keeps the predicate testable in node. */
export interface GuardableTarget {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: ((selector: string) => unknown) | null;
}

/** Tags whose focus means "the user is typing — keep your hands off the keys". */
const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Containers that own their own keyboard handling when the target sits inside. */
const DIALOG_SELECTOR = 'dialog, [role="dialog"], [aria-modal="true"]';

/**
 * Layers that suppress the list grammar document-wide while open. Includes
 * non-modal [role="dialog"] surfaces (the Popover primitive — tag pickers,
 * snooze menus, health breakdowns) which mount ONLY while open: the grammar
 * must not select/open rows underneath an open menu.
 */
const OPEN_MODAL_SELECTOR = 'dialog[open], [aria-modal="true"], [role="dialog"]';

/**
 * True when a keyboard shortcut must NOT fire for this event target: the user
 * is typing (input/textarea/select/contenteditable) or focus sits inside a
 * dialog that owns its own keys.
 */
export function isGuardedTarget(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as GuardableTarget;
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  if (EDITABLE_TAGS.has(tag)) return true;
  if (el.isContentEditable) return true;
  if (typeof el.closest === 'function' && el.closest(DIALOG_SELECTOR)) return true;
  return false;
}

/** Elements whose own Enter/Space behaviour must always win over the grammar. */
const ACTIVATION_SELECTOR = 'a, button, [role="button"], summary';

/**
 * True when the event target is (or sits inside) an interactive element that
 * natively consumes activation keys: links, buttons, role=button, summary, or
 * anything focusable via tabindex >= 0. The X/Space/Enter/E branches yield to
 * these so Tab + Enter/Space activates the control instead of acting on the
 * virtually-focused row. (Inputs/selects/textareas are already covered by
 * {@link isGuardedTarget}.)
 */
export function isActivationTarget(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as GuardableTarget;
  if (typeof el.closest !== 'function') return false;
  if (el.closest(ACTIVATION_SELECTOR)) return true;
  const focusable = el.closest('[tabindex]') as
    | { getAttribute?: (name: string) => string | null }
    | null
    | undefined;
  if (focusable && typeof focusable.getAttribute === 'function') {
    const raw = focusable.getAttribute('tabindex');
    const value = raw == null || raw.trim() === '' ? Number.NaN : Number(raw);
    if (Number.isFinite(value) && value >= 0) return true;
  }
  return false;
}

/** Minimal document slice so the check stays testable without a DOM. */
export interface ModalQueryable {
  querySelector(selector: string): unknown;
}

/**
 * True when a modal (native open dialog or anything aria-modal) is mounted —
 * the list grammar yields wholesale, even if focus escaped to the body.
 */
export function hasOpenModal(
  doc: ModalQueryable | undefined = typeof document !== 'undefined' ? document : undefined,
): boolean {
  if (!doc) return false;
  return Boolean(doc.querySelector(OPEN_MODAL_SELECTOR));
}

/* ------------------------------------------------------------------ */
/* Fuzzy matcher (pure) — shared with the command palette              */
/* ------------------------------------------------------------------ */

/**
 * Subsequence fuzzy score. Returns null when `query` is not an in-order
 * subsequence of `text`; otherwise a score where higher = better:
 *   +1 per matched character
 *   +4 when the match continues a consecutive run
 *   +8 when it lands on the very first character
 *   +6 when it lands on a word start (after space / - / _ / . / /)
 *   −1 per skipped character (capped at −3 per hop)
 * Empty queries score 0 (match-everything). Whitespace in the query is free.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  let score = 0;
  let searchFrom = 0;
  let prev = -2;
  for (const ch of q) {
    if (ch === ' ') continue;
    const at = t.indexOf(ch, searchFrom);
    if (at === -1) return null;
    score += 1;
    if (at === prev + 1) score += 4;
    if (at === 0) score += 8;
    else if (' /-_.'.includes(t[at - 1] ?? '')) score += 6;
    score -= Math.min(at - searchFrom, 3);
    prev = at;
    searchFrom = at + 1;
  }
  return score;
}

/**
 * Filter + rank `items` against `query`. Empty query returns every item in its
 * original order; otherwise best score first, ties broken by shorter text then
 * original position (stable, deterministic).
 */
export function fuzzyFilter<T>(query: string, items: T[], getText: (item: T) => string): T[] {
  if (!query.trim()) return [...items];
  return items
    .map((item, index) => ({ item, index, score: fuzzyScore(query, getText(item)) }))
    .filter((entry): entry is { item: T; index: number; score: number } => entry.score !== null)
    .sort(
      (a, b) =>
        b.score - a.score || getText(a.item).length - getText(b.item).length || a.index - b.index,
    )
    .map((entry) => entry.item);
}

/* ------------------------------------------------------------------ */
/* Focus-index arithmetic (pure)                                       */
/* ------------------------------------------------------------------ */

/**
 * Next focus index for a J/K (or arrow) move. From the "nothing focused"
 * state (−1), J lands on the first row and K on the last; otherwise the move
 * clamps at the list edges (no wrap — matches Linear).
 */
export function moveIndex(current: number, delta: 1 | -1, rowCount: number): number {
  if (rowCount <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : rowCount - 1;
  return Math.min(rowCount - 1, Math.max(0, current + delta));
}

/* ------------------------------------------------------------------ */
/* useListKeyboard — the hook every blotter consumes                   */
/* ------------------------------------------------------------------ */

export interface ListKeyboardOptions {
  /** Number of rows currently rendered (focus clamps when this shrinks). */
  rowCount: number;
  /** Fired after J/K/arrow moves the focus bar (next index). */
  onMove: (index: number) => void;
  /** X — toggle-select the focused row into the bulk bar. */
  onSelect: (index: number) => void;
  /** Enter — open the focused row. Space also lands here unless onPeek is set. */
  onOpen: (index: number) => void;
  /** Space — peek the focused row (peek drawer). Falls back to onOpen. */
  onPeek?: (index: number) => void;
  /** E — complete the focused row (task lists). Unbound when omitted. */
  onComplete?: (index: number) => void;
  /** Set false to suspend the grammar (e.g. while a peek drawer owns keys). */
  enabled?: boolean;
}

export interface ListKeyboardHandle {
  /** Current focus index (−1 = nothing focused). Pass to Blotter's focusIndex. */
  focusIndex: number;
  /** Sync focus from pointer interaction (row hover/click). */
  setFocusIndex: (index: number) => void;
}

/**
 * Mounts the J/K/X/Space/Enter(/E) list grammar on `window` for one list.
 * One listener per hook; reads options through a ref so it never re-binds;
 * detaches on unmount. Mount at most one enabled instance per page — when two
 * lists coexist (e.g. behind a peek drawer), toggle `enabled`.
 */
export function useListKeyboard(options: ListKeyboardOptions): ListKeyboardHandle {
  const [focusIndex, setFocusState] = useState(-1);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const focusRef = useRef(focusIndex);
  focusRef.current = focusIndex;

  // Clamp focus when the row count shrinks under it (filtering, deletes).
  useEffect(() => {
    const max = options.rowCount - 1;
    if (focusRef.current > max) {
      const next = max < 0 ? -1 : max;
      focusRef.current = next;
      setFocusState(next);
    }
  }, [options.rowCount]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const opts = optionsRef.current;
      if (opts.enabled === false) return;
      if (event.defaultPrevented) return;
      // Browser/OS chords pass through untouched (Cmd+J, Ctrl+K, Alt+...).
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isGuardedTarget(event.target)) return;
      if (hasOpenModal()) return;

      // Activation keys yield to focused interactive elements — Enter on a
      // Tab-focused pagination button must paginate, Space on a toolbar
      // button must activate it. J/K (below) stay live: moving the focus bar
      // while a button has focus is harmless.
      const interactive = isActivationTarget(event.target);

      const move = (delta: 1 | -1) => {
        if (opts.rowCount <= 0) return;
        event.preventDefault();
        const next = moveIndex(focusRef.current, delta, opts.rowCount);
        if (next !== focusRef.current) {
          focusRef.current = next;
          setFocusState(next);
          opts.onMove(next);
        }
      };

      switch (event.key) {
        case 'j':
        case 'J':
        case 'ArrowDown':
          move(1);
          return;
        case 'k':
        case 'K':
        case 'ArrowUp':
          move(-1);
          return;
        case 'x':
        case 'X':
          if (interactive || focusRef.current < 0) return;
          event.preventDefault();
          opts.onSelect(focusRef.current);
          return;
        case ' ':
          if (interactive || focusRef.current < 0) return;
          event.preventDefault();
          (opts.onPeek ?? opts.onOpen)(focusRef.current);
          return;
        case 'Enter':
          if (interactive || focusRef.current < 0) return;
          event.preventDefault();
          opts.onOpen(focusRef.current);
          return;
        case 'e':
        case 'E':
          if (interactive || !opts.onComplete || focusRef.current < 0) return;
          event.preventDefault();
          opts.onComplete(focusRef.current);
          return;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const setFocusIndex = useCallback((index: number) => {
    focusRef.current = index;
    setFocusState(index);
  }, []);

  return { focusIndex, setFocusIndex };
}
